---
title: "What Is React?"
slug: "what-is-react"
excerpt: "React, often referred to as React.js or ReactJS, is an open-source front-end JavaScript library developed by Meta (formerly Facebook) and a communi..."
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Development"
tags:
  - javascript
  - ui library
  - components
  - frontend
featuredImage: "https://k2bindia.com/wp-content/uploads/2015/08/React.png"
status: "published"
draft: false
---

## Short Answer

<ShortAnswer>
React, often referred to as React.js or ReactJS, is an open-source front-end JavaScript library developed by [Meta](# "Meta Platforms") (formerly Facebook) and a community of individual developers and companies. It is widely used for building interactive and dynamic web applications, including mobile applications through [React Native](# "React Native").
</ShortAnswer>


## Infobox

<InfoBox title="React">
<ul>
<li><strong>Developer(s):</strong> Meta</li>
<li><strong>Initial release:</strong> May 29, 2013</li>
<li><strong>Stable release:</strong> 18.2.0 \[edit")\]</li>
<li><strong>Repository:</strong> github.com/facebook/react</li>
<li><strong>Written in:</strong> JavaScript</li>
<li><strong>Type:</strong> JavaScript library</li>
<li><strong>License:</strong> MIT License</li>
<li><strong>Website:</strong> react.dev</li>
</ul>
</InfoBox>

## Overview

React, often referred to as React.js or ReactJS, is an open-source front-end JavaScript library developed by [Meta](# "Meta Platforms") (formerly Facebook) and a community of individual developers and companies. It is widely used for building interactive and dynamic web applications, including mobile applications through [React Native](# "React Native").

React's core features include the [component-based architecture](# "Component (computer programming)"), which enables developers to break down complex UIs into smaller, manageable pieces. Each component manages its own state and can be reused across different parts of an application, promoting code reusability and maintainability.

Another key aspect of React is its [unidirectional data flow](# "Unidirectional data flow"), where data moves in a single direction—from parent to child components—making the application's behavior more predictable and easier to debug.

## History / Background

React was first created by [Jordan Walke](# "Jordan Walke"), a software engineer at Meta, in 2011. It was initially developed to address the challenges of building large-scale applications with dynamic data, particularly for Facebook's news feed. The library was first deployed on Facebook's production servers in 2011 and later on Instagram in 2012.

In May 2013, React was publicly released as an open-source project under the [MIT License](# "MIT License"). This release marked the beginning of React's widespread adoption in the web development community. Over the years, React has evolved significantly, with major updates such as React 16 in 2017, which introduced [Fiber](# "Fiber (React)"), a new reconciliation engine, and React 18 in 2022, which added features like [concurrent rendering](# "Concurrent rendering") and [server-side rendering](# "Server-side rendering") (SSR).

## How It Works

### Virtual DOM

React uses a [virtual DOM](# "Virtual DOM") to optimize rendering performance. Instead of directly updating the browser's DOM, React creates a lightweight copy of the DOM in memory, known as the virtual DOM. When data changes, React compares the virtual DOM with the previous version (a process called [diffing](# "Diffing algorithm")) and calculates the minimal set of changes required to update the actual DOM. This approach minimizes the number of expensive DOM operations, leading to improved performance.

### Components

React applications are built using components, which are self-contained pieces of code that manage their own state and render UI elements. Components can be classified into two types:

- **Function Components:** Simple JavaScript functions that return React elements. With the introduction of [Hooks](# "Hooks (React)") in React 16.8, function components can now manage state and side effects.
- **Class Components:** ES6 classes that extend `React.Component` and include lifecycle methods like `componentDidMount` and `componentDidUpdate`.
 
### JSX

React uses [JSX](# "JSX"), a syntax extension for JavaScript, to describe what the UI should look like. JSX allows developers to write HTML-like code directly within JavaScript, making it easier to visualize the component structure. For example:

 ```

 <h1>Hello, React!</h1>
 
```

Under the hood, JSX is transformed into regular JavaScript function calls by tools like [Babel](# "Babel (compiler)").

### State and Props

React components can manage [state](# "State (computer science)") and receive [props](# "Property (programming)") (properties). State refers to data that changes over time within a component, while props are read-only data passed from parent to child components. The distinction between state and props is fundamental to React's data flow model.

## Important Facts

<FactList>
<ul>
<li><strong>Declarative Syntax:</strong> React encourages a declarative programming style, where developers describe the desired outcome rather than the steps to achieve it. This makes the code more intuitive and easier to understand.</li>
<li><strong>One-Way Data Binding:</strong> Data flows in a single direction in React, from parent to child components, which simplifies debugging and makes the application's behavior more predictable.</li>
<li><strong>Rich Ecosystem:</strong> React has a vast ecosystem of libraries, tools, and frameworks, such as [Redux](# "Redux (JavaScript library)"), [Next.js](# "Next.js"), and [Material-UI](# "Material-UI"), that extend its functionality.</li>
<li><strong>Cross-Platform Development:</strong> React can be used to build not only web applications but also mobile applications ([React Native](# "React Native")) and even desktop applications ([Electron](# "Electron (software framework)")).</li>
<li><strong>Strong Community Support:</strong> React has one of the largest and most active developer communities, with extensive documentation, tutorials, and third-party resources available online.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "2011", title: "React is developed internally", description: "React is developed internally at Meta for Facebook's news feed." },
  { date: "2012", title: "React is deployed on", description: "React is deployed on Instagram's production servers." },
  { date: "May 2013", title: "React is publicly released", description: "React is publicly released as an open-source project." },
  { date: "2015", title: "React Native is introduced", description: "React Native is introduced, enabling mobile app development with React." },
  { date: "2017", title: "React 16 is released", description: "React 16 is released, introducing the Fiber reconciliation engine." },
  { date: "2019", title: "Hooks are introduced in", description: "Hooks are introduced in React 16.8, allowing function components to manage state." },
  { date: "2022", title: "React 18 is released", description: "React 18 is released, adding concurrent rendering and server-side rendering features." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>Front</strong> – end development</li>
<li><strong>Single</strong> – page application (SPA)</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>What is the difference between React and React DOM?</summary>
<p>React is the core library that includes the component model and rendering logic, while React DOM is a separate package that provides DOM-specific methods for rendering React components in the browser. React DOM is used to bridge the gap between React and the browser's DOM.</p>
</details>
<details>
<summary>Is React a framework or a library?</summary>
<p>React is technically a library because it focuses on a specific aspect of development—building user interfaces. However, it is often referred to as a framework due to its extensive ecosystem and the way developers use it to build full-fledged applications.</p>
</details>
<details>
<summary>What are the advantages of using React?</summary>
<p>React offers several advantages, including:</p>
<ul>
<li>Improved performance due to the virtual DOM.</li>
<li>Reusable components that promote code maintainability.</li>
<li>A strong and active community with extensive resources.</li>
<li>Flexibility and compatibility with other libraries and frameworks.</li>
<li>Support for server-side rendering (SSR) and static site generation (SSG).</li>
</ul>
</details>
<details>
<summary>What is the difference between state and props in React?</summary>
<p>State refers to data that changes over time within a component and is managed internally by the component. Props are read-only data passed from parent to child components and are used to pass data down the component hierarchy. State is mutable, while props are immutable.</p>
</details>
<details>
<summary>How do I get started with React?</summary>
<p>To get started with React, you can follow these steps:</p>
<p>1. Install [Node.js](# "Node.js") and [npm](# "npm") (Node Package Manager) on your system.</p>
<p>2. Create a new React project using [Create React App](# "Create React App") or a similar tool like [Vite](# "Vite").</p>
<p>3. Start building components by writing JavaScript or JSX code.</p>
<p>4. Use tools like [Babel](# "Babel (compiler)") and [Webpack](# "Webpack") to transpile and bundle your code.</p>
<p>5. Deploy your application to a hosting service like [Vercel](# "Vercel"), [Netlify](# "Netlify"), or [GitHub Pages](# "GitHub Pages").</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>React Official Documentation</li>
<li>React GitHub Repository</li>
<li>Wikipedia: React (JavaScript library))</li>
<li>Why React? (Official Blog Post)</li>
<li>React 16 Release Blog Post</li>
<li>React 18 Release Blog Post</li>
</ol>
</ReferenceList>

